import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useOnboardingStore, OnboardingRequest } from '../../../store/onboardingStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { Card, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

const STEPS = [
    { id: 1, label: 'Personal', icon: '' },
    { id: 2, label: 'Job', icon: '' },
    { id: 3, label: 'Compliance', icon: '' },
    { id: 4, label: 'Bank', icon: '' },
    { id: 5, label: 'Experience', icon: '' },
    { id: 6, label: 'Family', icon: '' },
    { id: 7, label: 'Documents', icon: '' }
];

export function OnboardingWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { requests, isLoading, updateRequest, verifyDocument } = useOnboardingStore();
  const { createEmployee } = useEmployeeStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [activeRequest, setActiveRequest] = useState<OnboardingRequest | null>(null);

  useEffect(() => {
    const req = requests.find(r => r.id === id);
    if (req) {
      setActiveRequest(req);
      setCurrentStep(req.currentStep);
    }
  }, [id, requests]);

  const handleApproveAll = async () => {
      if (!activeRequest) return;

      try {
          await updateRequest(id!, { status: 'approved' });

          const nameParts = (activeRequest.candidateName || '').trim().split(' ');
          const firstName = nameParts[0] || 'Team';
          const lastName = nameParts.slice(1).join(' ') || 'Member';

          try {
              await createEmployee({
                  firstName,
                  lastName,
                  email: activeRequest.email,
                  department: activeRequest.department || 'Engineering',
                  designation: activeRequest.designation || 'Team Member',
                  joinDate: activeRequest.joiningDate ? new Date(activeRequest.joiningDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
                  status: 'active',
              });
              toast.success(`Onboarding approved! ${firstName} ${lastName} provisioned in Employee Directory.`);
          } catch (empErr: any) {
              toast.success('Onboarding stage approved by HR.');
          }

          navigate('/people/onboarding');
      } catch (err) {
          toast.error('Failed to complete approval');
      }
  };

  const handleDocumentAction = (docId: string, status: 'approved' | 'rejected') => {
      verifyDocument(id!, docId, status);
      toast.success(`Document ${status === 'approved' ? 'verified' : 'rejected'}`);
  };

  if (!activeRequest) {
    return (
        <div className="flex flex-col items-center justify-center p-12 animate-pulse">
            <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary-500 animate-spin mb-4" />
            <p className="text-muted-foreground font-semibold text-xs">Fetching Request Data...</p>
        </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-4">
            <Link to="/people/onboarding" className="p-3 bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground transition-all">←</Link>
            <div>
                 <div className="flex items-center gap-3">
                    <h1 className="tracking-tight text-foreground font-display text-page-title">{activeRequest.candidateName}</h1>
                    <Badge variant={activeRequest.status === 'under-review' ? 'warning' : 'primary'}>{activeRequest.status}</Badge>
                 </div>
                 <p className="text-xs text-muted-foreground font-semibold mt-1">
                     Position: {activeRequest.designation} • Dept: {activeRequest.department}
                 </p>
            </div>
        </div>
        <div className="flex gap-2">
            <Button variant="secondary" size="sm" className="bg-destructive/10 border-destructive/20 text-destructive hover:bg-destructive/30" onClick={() => toast.error('Onboarding withdrawn.')}>Withdrawn Request</Button>
            <Button variant="success" size="sm" onClick={handleApproveAll}>
                Final HR Approval             </Button>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex justify-between relative px-2 mb-8">
            <div className="absolute top-1/2 left-0 right-0 h-px bg-border-12 -z-10" />
            {STEPS.map(step => (
                <div 
                    key={step.id} 
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex flex-col items-center gap-2 cursor-pointer transition-all duration-300 group ${currentStep === step.id ? 'scale-110' : 'opacity-40 hover:opacity-100'}`}
                >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-sm border-2 transition-all ${
                        currentStep === step.id ? 'bg-primary border-primary text-white' : 
                        step.id < activeRequest.currentStep ? 'bg-success/20 border-success/40 text-success' : 'bg-card border-border text-muted-foreground'
                    }`}>
                        {step.id < activeRequest.currentStep ? '' : step.icon}
                    </div>
                    <span className={`text-xs font-semibold ${currentStep === step.id ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {step.label}
                    </span>
                </div>
            ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Main Section */}
        <div className="lg:col-span-2 space-y-6">
            <Card className="bg-card/40 border-border animate-in slide-in-from-bottom-4 duration-500">
                <CardContent className="p-8">
                     <div className="flex justify-between items-center mb-10 pb-4 border-b border-border">
                        <h2 className="text-foreground font-display text-section-title">{STEPS[currentStep-1].label} Information</h2>
                        <span className="text-xs text-muted-foreground font-semibold">Section {currentStep} of 7</span>
                     </div>

                     {/* Dynamic Content based on Steps */}
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                        {currentStep === 1 && (
                            <>
                                {[
                                    { label: 'Full Name', value: activeRequest.candidateName },
                                    { label: 'DOB', value: activeRequest.personalDetails?.dob || '--' },
                                    { label: 'Gender', value: activeRequest.personalDetails?.gender || '--' },
                                    { label: 'Blood Group', value: activeRequest.personalDetails?.bloodGroup || '--' },
                                    { label: 'Marital Status', value: activeRequest.personalDetails?.maritalStatus || '--' },
                                    { label: 'Nationality', value: activeRequest.personalDetails?.nationality || '--' },
                                    { label: 'Emergency Contact', value: activeRequest.personalDetails?.emergencyContact?.name || '--', sub: activeRequest.personalDetails?.emergencyContact?.phone },
                                    { label: 'Residential Address', value: activeRequest.personalDetails?.currentAddress || '--', full: true }
                                ].map((item, i) => (
                                    <div key={i} className={`space-y-1 ${item.full ? 'md:col-span-2' : ''}`}>
                                        <p className="text-xs font-semibold text-muted-foreground">{item.label}</p>
                                        <p className="text-sm font-semibold text-foreground">{item.value}</p>
                                        {item.sub && <p className="text-xs text-muted-foreground mt-0.5">{item.sub}</p>}
                                    </div>
                                ))}
                            </>
                        )}

                        {currentStep === 2 && (
                             <>
                                {[
                                    { label: 'Joining Date', value: activeRequest.jobDetails?.dateOfJoining || activeRequest.joiningDate },
                                    { label: 'Department', value: activeRequest.department },
                                    { label: 'Designation', value: activeRequest.designation },
                                    { label: 'Employment Type', value: activeRequest.jobDetails?.employmentType || '--' },
                                    { label: 'Work Location', value: activeRequest.jobDetails?.workLocation || '--' },
                                    { label: 'Reporting Manager', value: activeRequest.jobDetails?.reportingManager || '--' },
                                    { label: 'HR SPOC', value: activeRequest.jobDetails?.hrSpoc || '--' },
                                    { label: 'Salary/CTC', value: activeRequest.jobDetails?.salary || '--' }
                                ].map((item, i) => (
                                    <div key={i} className="space-y-1">
                                        <p className="text-xs font-semibold text-muted-foreground">{item.label}</p>
                                        <p className="text-sm font-semibold text-foreground">{item.value}</p>
                                    </div>
                                ))}
                            </>
                        )}

                        {currentStep === 7 && (
                             <div className="md:col-span-2 space-y-6">
                                {activeRequest.documents?.map(doc => (
                                    <div key={doc.id} className="p-5 bg-background border border-border rounded-lg flex justify-between items-center group hover:border-primary/40 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-lg bg-card flex items-center justify-center text-xl border border-border group-hover:scale-105 transition-transform"></div>
                                            <div>
                                                <p className="text-sm font-semibold text-foreground">{doc.name}</p>
                                                <div className="flex gap-2 items-center mt-1">
                                                    <span className="text-xs text-muted-foreground font-semibold">{doc.type}</span>
                                                    <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'error' : 'warning'} className="text-xs">{doc.status}</Badge>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="secondary" size="xs" className="text-xs font-semibold" onClick={() => toast.success('Document Preview triggered.')}>Preview</Button>
                                            <Button variant="secondary" size="xs" className="text-xs font-semibold bg-success/20 text-success hover:bg-success/30 border-none" onClick={() => handleDocumentAction(doc.id, 'approved')}>Verify</Button>
                                            <Button variant="secondary" size="xs" className="text-xs font-semibold bg-destructive/20 text-destructive hover:bg-destructive/30 border-none" onClick={() => handleDocumentAction(doc.id, 'rejected')}>Reject</Button>
                                        </div>
                                    </div>
                                ))}
                                {!activeRequest.documents && (
                                    <div className="text-center py-12 px-8 border border-dashed border-border rounded-lg">
                                        <p className="text-muted-foreground text-xs font-semibold">Candidate has not yet uploaded documentation.</p>
                                        <Button variant="primary" size="sm" className="mt-4" onClick={() => toast.success('Reminder sent to candidate via Email/SMS.')}>Send Reminder</Button>
                                    </div>
                                )}
                             </div>
                        )}

                        {/* Fallback for other steps */}
                        {currentStep !== 1 && currentStep !== 2 && currentStep !== 7 && (
                            <div className="md:col-span-2 text-center py-12">
                                <p className="text-muted-foreground text-xs">Awaiting candidate detail entry for this section.</p>
                            </div>
                        )}
                     </div>

                     <div className="mt-12 pt-8 border-t border-border flex justify-between">
                         <Button variant="secondary" disabled={currentStep === 1} onClick={() => setCurrentStep(prev => prev - 1)}>← Previous Step</Button>
                         <Button variant="primary" disabled={currentStep === 7} onClick={() => setCurrentStep(prev => prev + 1)}>Next Section →</Button>
                     </div>
                </CardContent>
            </Card>

            {/* Audit Trail Sidebar on Right */}
        </div>

        <div className="space-y-6">
             <Card className="bg-card/40 border-border">
                <CardContent className="p-6">
                    <h3 className="text-foreground mb-4 border-b border-border pb-2 font-display text-card-title">IT & Admin Activation</h3>
                    <div className="space-y-4">
                        {[
                            { label: 'Workforce ID Creation', status: 'completed' },
                            { label: 'Corporate Email Generation', status: 'pending' },
                            { label: 'Laptop Asset Allocation', status: 'pending' },
                            { label: 'ID Card Print Order', status: 'not-initiated' },
                            { label: 'Welcome Kit Delivery', status: 'not-initiated' }
                        ].map((item, i) => (
                            <div key={i} className="flex justify-between items-center group">
                                <span className={`text-xs font-semibold tracking-tight ${item.status === 'completed' ? 'text-success' : 'text-muted-foreground'}`}>{item.label}</span>
                                <input type="checkbox" checked={item.status === 'completed'} readOnly className="appearance-none w-4 h-4 rounded-md bg-background border border-border checked:bg-primary cursor-pointer" />
                            </div>
                        ))}
                    </div>
                </CardContent>
             </Card>

             <Card className="bg-card/40 border-border">
                <CardContent className="p-6">
                    <h3 className="text-foreground mb-4 border-b border-border pb-2 font-display text-card-title">Activity History</h3>
                    <div className="space-y-6">
                         {[
                             { action: 'Offer Accepted', time: '2 Days ago', label: 'Candidate' },
                             { action: 'Form Submission Started', time: 'Yesterday', label: 'System' },
                             { action: 'Initial Verification', time: '4 hrs ago', label: 'HR Admin' }
                         ].map((entry, i) => (
                             <div key={i} className="relative flex gap-3">
                                 {i !== 2 && <div className="absolute left-1.5 top-5 bottom-0 w-px bg-border-12" />}
                                 <div className={`w-3 h-3 rounded-full mt-1 border-2 border-surface-800 transition-colors ${i === 0 ? 'bg-success' : 'bg-primary'}`} />
                                 <div className="flex-1">
                                     <p className="text-xs font-semibold text-foreground">{entry.action}</p>
                                     <div className="flex justify-between items-center mt-1">
                                        <p className="text-xs text-muted-foreground">{entry.time}</p>
                                        <p className="text-xs text-muted-foreground font-semibold border border-border px-1.5 rounded-md">{entry.label}</p>
                                     </div>
                                 </div>
                             </div>
                         ))}
                    </div>
                </CardContent>
             </Card>
        </div>
      </div>
    </div>
  );
}
